import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Scissors, Plus, Calendar, DollarSign, ChevronDown, ChevronUp, Pencil, Trash2, X, Check, Clock, Upload, FileText, Image } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper to parse fractions like "17 5/8" to decimal
const parseFraction = (str) => {
  if (!str || str === "0") return 0;
  str = String(str).trim();
  
  // Handle pure fractions like "5/8"
  if (str.includes("/") && !str.includes(" ")) {
    const [num, den] = str.split("/").map(Number);
    return num / den;
  }
  
  // Handle mixed fractions like "17 5/8"
  const parts = str.split(" ");
  if (parts.length === 2 && parts[1].includes("/")) {
    const whole = parseFloat(parts[0]) || 0;
    const [num, den] = parts[1].split("/").map(Number);
    return whole + (num / den);
  }
  
  return parseFloat(str) || 0;
};

// Calculate difference and format it
const calculateDiff = (prev, current) => {
  const prevVal = parseFraction(prev);
  const currVal = parseFraction(current);
  const diff = currVal - prevVal;
  
  if (diff === 0) return { value: "0", display: "0", color: "text-muted-foreground" };
  
  const sign = diff > 0 ? "+" : "";
  const absVal = Math.abs(diff);
  
  // Format as fraction if needed
  let display = `${sign}${diff.toFixed(2)}`;
  
  // Common fractions
  const fractions = {
    0.125: "1/8", 0.25: "1/4", 0.375: "3/8", 0.5: "1/2",
    0.625: "5/8", 0.75: "3/4", 0.875: "7/8"
  };
  
  const decimal = absVal % 1;
  const whole = Math.floor(absVal);
  
  if (fractions[decimal]) {
    if (whole === 0) {
      display = `${sign}${fractions[decimal]}`;
    } else {
      display = `${sign}${whole} ${fractions[decimal]}`;
    }
  }
  
  return {
    value: diff,
    display: display,
    color: diff > 0 ? "text-green-400" : "text-red-400"
  };
};

// Convert decimal to fraction string
const decimalToFraction = (decimal) => {
  if (decimal === 0) return "0";
  
  const sign = decimal < 0 ? "-" : "";
  const absVal = Math.abs(decimal);
  const whole = Math.floor(absVal);
  const frac = absVal - whole;
  
  const fractions = {
    0.125: "1/8", 0.25: "1/4", 0.375: "3/8", 0.5: "1/2",
    0.625: "5/8", 0.75: "3/4", 0.875: "7/8"
  };
  
  // Find closest fraction
  let closestFrac = "";
  let minDiff = 1;
  for (const [dec, str] of Object.entries(fractions)) {
    const diff = Math.abs(frac - parseFloat(dec));
    if (diff < minDiff) {
      minDiff = diff;
      closestFrac = str;
    }
  }
  
  if (minDiff < 0.05 && closestFrac) {
    if (whole === 0) {
      return `${sign}${closestFrac}`;
    }
    return `${sign}${whole} ${closestFrac}`;
  }
  
  if (whole === 0 && frac === 0) return "0";
  if (frac < 0.05) return `${sign}${whole}`;
  
  return `${sign}${absVal.toFixed(2)}`;
};

// Adjustment options for dropdown
const ADJUSTMENT_OPTIONS = [
  { value: "0", label: "No Change" },
  { value: "0.125", label: "+1/8" },
  { value: "0.25", label: "+1/4" },
  { value: "0.375", label: "+3/8" },
  { value: "0.5", label: "+1/2" },
  { value: "0.625", label: "+5/8" },
  { value: "0.75", label: "+3/4" },
  { value: "0.875", label: "+7/8" },
  { value: "1", label: "+1" },
  { value: "1.25", label: "+1 1/4" },
  { value: "1.5", label: "+1 1/2" },
  { value: "1.75", label: "+1 3/4" },
  { value: "2", label: "+2" },
  { value: "-0.125", label: "-1/8" },
  { value: "-0.25", label: "-1/4" },
  { value: "-0.375", label: "-3/8" },
  { value: "-0.5", label: "-1/2" },
  { value: "-0.625", label: "-5/8" },
  { value: "-0.75", label: "-3/4" },
  { value: "-0.875", label: "-7/8" },
  { value: "-1", label: "-1" },
  { value: "-1.25", label: "-1 1/4" },
  { value: "-1.5", label: "-1 1/2" },
  { value: "-1.75", label: "-1 3/4" },
  { value: "-2", label: "-2" },
];

// Default measurement reference fields with alteration tracking
const DEFAULT_JACKET_MEASUREMENTS = [
  { name: "Chest", prev_value: "0", current_value: "0", adjustment: "0", fee: 30.00, selected: false },
  { name: "Stomach", prev_value: "0", current_value: "0", adjustment: "0", fee: 30.00, selected: false },
  { name: "Hip", prev_value: "0", current_value: "0", adjustment: "0", fee: 30.00, selected: false },
  { name: "Bicep", prev_value: "0", current_value: "0", adjustment: "0", fee: 30.00, selected: false },
  { name: "Wrist", prev_value: "0", current_value: "0", adjustment: "0", fee: 30.00, selected: false },
  { name: "Shoulder", prev_value: "0", current_value: "0", adjustment: "0", fee: 30.00, selected: false },
  { name: "Sleeve L", prev_value: "0", current_value: "0", adjustment: "0", fee: 30.00, selected: false },
  { name: "Sleeve R", prev_value: "0", current_value: "0", adjustment: "0", fee: 30.00, selected: false },
  { name: "Front Len", prev_value: "0", current_value: "0", adjustment: "0", fee: 30.00, selected: false },
  { name: "Back Len", prev_value: "0", current_value: "0", adjustment: "0", fee: 30.00, selected: false },
  { name: "1st Btn", prev_value: "0", current_value: "0", adjustment: "0", fee: 30.00, selected: false },
  { name: "Half Back", prev_value: "0", current_value: "0", adjustment: "0", fee: 30.00, selected: false },
  { name: "Lower Collar", prev_value: "0", current_value: "0", adjustment: "0", fee: 30.00, selected: false },
];

const DEFAULT_PANTS_MEASUREMENTS = [
  { name: "Waist", prev_value: "0", current_value: "0", adjustment: "0", fee: 20.00, selected: false },
  { name: "Hip", prev_value: "0", current_value: "0", adjustment: "0", fee: 20.00, selected: false },
  { name: "Thigh", prev_value: "0", current_value: "0", adjustment: "0", fee: 20.00, selected: false },
  { name: "Knee", prev_value: "0", current_value: "0", adjustment: "0", fee: 20.00, selected: false },
  { name: "Crotch", prev_value: "0", current_value: "0", adjustment: "0", fee: 20.00, selected: false },
  { name: "L Outseam", prev_value: "0", current_value: "0", adjustment: "0", fee: 20.00, selected: false },
  { name: "R Outseam", prev_value: "0", current_value: "0", adjustment: "0", fee: 20.00, selected: false },
  { name: "Hem", prev_value: "0", current_value: "0", adjustment: "0", fee: 20.00, selected: false },
];

const DEFAULT_VEST_MEASUREMENTS = [
  { name: "Chest", prev_value: "0", current_value: "0", adjustment: "0", fee: 25.00, selected: false },
  { name: "Stomach", prev_value: "0", current_value: "0", adjustment: "0", fee: 25.00, selected: false },
  { name: "Back Len", prev_value: "0", current_value: "0", adjustment: "0", fee: 25.00, selected: false },
  { name: "Front Len", prev_value: "0", current_value: "0", adjustment: "0", fee: 25.00, selected: false },
  { name: "1st Btn", prev_value: "0", current_value: "0", adjustment: "0", fee: 25.00, selected: false },
  { name: "Bottom", prev_value: "0", current_value: "0", adjustment: "0", fee: 25.00, selected: false },
];

const STATUS_COLORS = {
  pending: "bg-yellow-500/20 text-yellow-400",
  in_progress: "bg-blue-500/20 text-blue-400",
  completed: "bg-green-500/20 text-green-400"
};

const AdminAlterationsPage = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    jacket_measurements: true,
    pants_measurements: false,
    vest_measurements: false
  });
  
  // For importing measurements
  const [measurements, setMeasurements] = useState([]);
  const [showMeasurementPicker, setShowMeasurementPicker] = useState(false);
  const [uploadedImages, setUploadedImages] = useState([]);
  const fileInputRef = useRef(null);
  
  // For importing from MTM Orders
  const [mtmOrders, setMtmOrders] = useState([]);
  const [showMTMPicker, setShowMTMPicker] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    tag: "",
    client_name: "",
    client_email: "",
    due_date: "",
    status: "pending",
    is_free: false,
    jacket_measurements: JSON.parse(JSON.stringify(DEFAULT_JACKET_MEASUREMENTS)),
    pants_measurements: JSON.parse(JSON.stringify(DEFAULT_PANTS_MEASUREMENTS)),
    vest_measurements: JSON.parse(JSON.stringify(DEFAULT_VEST_MEASUREMENTS)),
    jacket_remarks: "",
    pants_remarks: "",
    reference_images: []
  });

  useEffect(() => {
    const adminAuth = sessionStorage.getItem("adminAuth");
    if (adminAuth !== "true") {
      navigate("/admin");
    } else {
      setIsAuthenticated(true);
      fetchOrders();
      fetchMeasurements();
      fetchMTMOrders();
    }
  }, [navigate]);

  const fetchMeasurements = async () => {
    try {
      const response = await axios.get(`${API}/measurements`);
      setMeasurements(response.data);
    } catch (error) {
      console.error("Failed to fetch measurements:", error);
    }
  };

  const fetchMTMOrders = async () => {
    try {
      const response = await axios.get(`${API}/mtm-orders`);
      // Filter to only show submitted orders (not drafts)
      const submittedOrders = response.data.filter(o => o.status !== 'draft');
      setMtmOrders(submittedOrders);
    } catch (error) {
      console.error("Failed to fetch MTM orders:", error);
    }
  };

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/alterations`);
      setOrders(response.data);
    } catch (error) {
      console.error("Failed to fetch alterations:", error);
      toast.error("Failed to load alterations");
    } finally {
      setLoading(false);
    }
  };

  // Handle image upload (multiple)
  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      files.forEach(file => {
        const reader = new FileReader();
        reader.onloadend = () => {
          setUploadedImages(prev => [...prev, reader.result]);
          setFormData(prev => ({ 
            ...prev, 
            reference_images: [...prev.reference_images, reader.result] 
          }));
        };
        reader.readAsDataURL(file);
      });
      toast.success(`${files.length} image(s) added`);
      // Reset input to allow uploading same file again
      e.target.value = '';
    }
  };

  // Remove a specific image
  const removeImage = (index) => {
    setUploadedImages(prev => prev.filter((_, i) => i !== index));
    setFormData(prev => ({
      ...prev,
      reference_images: prev.reference_images.filter((_, i) => i !== index)
    }));
    toast.success("Image removed");
  };

  // Import measurements from submitted client measurements
  const importFromMeasurement = (measurement) => {
    setFormData(prev => {
      const updated = { ...prev };
      updated.client_name = measurement.full_name || measurement.name || "";
      updated.client_email = measurement.email || "";
      
      return updated;
    });
    
    setShowMeasurementPicker(false);
    toast.success(`Imported client info from ${measurement.full_name || measurement.email}`);
  };

  // Import measurements from MTM Order
  const importFromMTMOrder = (order) => {
    setFormData(prev => {
      const updated = { ...prev };
      
      // Set client info
      updated.client_name = order.client_name || "";
      updated.client_email = order.client_email || "";
      updated.tag = `MTM-${order.order_number}`;
      
      // Import jacket measurements if available
      if (order.jacket_measurements) {
        const jm = order.jacket_measurements.measurements || order.jacket_measurements.starting_specs || {};
        updated.jacket_measurements = updated.jacket_measurements.map(m => {
          const mapping = {
            "Chest": jm.chest,
            "Stomach": jm.stomach,
            "Hip": jm.seat,
            "Bicep": jm.bicep,
            "Shoulder": jm.shoulder,
            "Sleeve L": jm.sleeve_length,
            "Sleeve R": jm.sleeve_length,
            "Front Len": jm.front_length,
            "Back Len": jm.back_length,
          };
          if (mapping[m.name] !== undefined) {
            return { ...m, prev_value: String(mapping[m.name] || 0), current_value: String(mapping[m.name] || 0) };
          }
          return m;
        });
      }
      
      // Import pants measurements if available
      if (order.pant_measurements) {
        const pm = order.pant_measurements.measurements || order.pant_measurements.starting_specs || {};
        updated.pants_measurements = updated.pants_measurements.map(m => {
          const mapping = {
            "Waist": pm.waist,
            "Hip": pm.seat,
            "Thigh": pm.thigh,
            "Knee": pm.knee,
            "Crotch": pm.urise,
            "L Outseam": pm.pant_length,
            "R Outseam": pm.pant_length,
            "Hem": pm.bottom,
          };
          if (mapping[m.name] !== undefined) {
            return { ...m, prev_value: String(mapping[m.name] || 0), current_value: String(mapping[m.name] || 0) };
          }
          return m;
        });
      }
      
      // Import vest measurements if available
      if (order.vest_measurements) {
        const vm = order.vest_measurements.measurements || order.vest_measurements.starting_specs || {};
        updated.vest_measurements = updated.vest_measurements.map(m => {
          const mapping = {
            "Chest": vm.chest,
            "Stomach": vm.stomach,
            "Back Len": vm.back_length,
            "Front Len": vm.front_length,
          };
          if (mapping[m.name] !== undefined) {
            return { ...m, prev_value: String(mapping[m.name] || 0), current_value: String(mapping[m.name] || 0) };
          }
          return m;
        });
      }
      
      // Copy remarks if available
      if (order.jacket_measurements?.remarks) {
        updated.jacket_remarks = order.jacket_measurements.remarks;
      }
      if (order.pant_measurements?.remarks) {
        updated.pants_remarks = order.pant_measurements.remarks;
      }
      
      return updated;
    });
    
    // Expand sections based on what was imported
    setExpandedSections({
      jacket_measurements: !!order.jacket_measurements,
      pants_measurements: !!order.pant_measurements,
      vest_measurements: !!order.vest_measurements
    });
    
    setShowMTMPicker(false);
    toast.success(`Imported measurements from order ${order.order_number}`);
  };

  const resetForm = () => {
    setFormData({
      tag: "",
      client_name: "",
      client_email: "",
      due_date: "",
      status: "pending",
      jacket_measurements: JSON.parse(JSON.stringify(DEFAULT_JACKET_MEASUREMENTS)),
      pants_measurements: JSON.parse(JSON.stringify(DEFAULT_PANTS_MEASUREMENTS)),
      vest_measurements: JSON.parse(JSON.stringify(DEFAULT_VEST_MEASUREMENTS)),
      jacket_remarks: "",
      pants_remarks: "",
      reference_images: []
    });
    setUploadedImages([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.tag || !formData.due_date) {
      toast.error("Please fill in tag and due date");
      return;
    }

    try {
      if (editingOrder) {
        await axios.put(`${API}/alterations/${editingOrder.id}`, formData);
        toast.success("Alteration updated successfully");
      } else {
        await axios.post(`${API}/alterations`, formData);
        toast.success("Alteration created successfully");
      }
      
      setShowForm(false);
      setEditingOrder(null);
      resetForm();
      fetchOrders();
    } catch (error) {
      console.error("Failed to save alteration:", error);
      toast.error("Failed to save alteration");
    }
  };

  const handleEdit = (order) => {
    setEditingOrder(order);
    setFormData({
      tag: order.tag,
      client_name: order.client_name || "",
      client_email: order.client_email || "",
      due_date: order.due_date,
      status: order.status,
      jacket_measurements: order.jacket_measurements?.length ? order.jacket_measurements : JSON.parse(JSON.stringify(DEFAULT_JACKET_MEASUREMENTS)),
      pants_measurements: order.pants_measurements?.length ? order.pants_measurements : JSON.parse(JSON.stringify(DEFAULT_PANTS_MEASUREMENTS)),
      vest_measurements: order.vest_measurements?.length ? order.vest_measurements : JSON.parse(JSON.stringify(DEFAULT_VEST_MEASUREMENTS)),
      jacket_remarks: order.jacket_remarks || "",
      pants_remarks: order.pants_remarks || ""
    });
    setShowForm(true);
  };

  const handleDelete = async (orderId) => {
    if (!window.confirm("Are you sure you want to delete this alteration?")) return;
    
    try {
      await axios.delete(`${API}/alterations/${orderId}`);
      toast.success("Alteration deleted");
      fetchOrders();
    } catch (error) {
      console.error("Failed to delete alteration:", error);
      toast.error("Failed to delete alteration");
    }
  };

  const updateAlterationItem = (section, index, field, value) => {
    setFormData(prev => {
      const updated = { ...prev };
      updated[section] = [...prev[section]];
      updated[section][index] = { ...updated[section][index], [field]: value };
      return updated;
    });
  };

  // Handle adjustment dropdown change - calculates Current ALT from Prev ALT + adjustment
  const handleAdjustmentChange = (section, index, adjustmentValue) => {
    setFormData(prev => {
      const updated = { ...prev };
      updated[section] = [...prev[section]];
      const item = { ...updated[section][index] };
      
      item.adjustment = adjustmentValue;
      
      // Calculate new current value from prev + adjustment
      const prevVal = parseFraction(item.prev_value);
      const adjustment = parseFloat(adjustmentValue) || 0;
      const newVal = prevVal + adjustment;
      
      // Convert to fraction format
      item.current_value = decimalToFraction(newVal);
      
      // Auto-select if adjustment is not 0
      if (adjustment !== 0) {
        item.selected = true;
      }
      
      updated[section][index] = item;
      return updated;
    });
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const calculateTotal = () => {
    let total = 0;
    [
      ...formData.jacket_measurements,
      ...formData.pants_measurements,
      ...formData.vest_measurements
    ].forEach(item => {
      if (item.selected) total += item.fee;
    });
    return total;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric"
    });
  };

  const getDaysUntilDue = (dueDate) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const due = new Date(dueDate);
    due.setHours(0, 0, 0, 0);
    return Math.ceil((due - today) / (1000 * 60 * 60 * 24));
  };

  if (!isAuthenticated) return null;

  return (
    <div className="min-h-screen pt-20">
      {/* Header */}
      <section className="py-6 border-b border-white/5">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <button
              onClick={() => navigate("/admin")}
              className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-4 transition-colors"
            >
              <ArrowLeft size={16} />
              Back to Dashboard
            </button>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <Scissors size={24} className="text-primary" />
                </div>
                <div>
                  <h1 className="font-serif text-2xl sm:text-3xl tracking-tight">Alterations</h1>
                  <p className="text-sm text-muted-foreground">{orders.length} alteration orders</p>
                </div>
              </div>
              <Button
                onClick={() => { setEditingOrder(null); resetForm(); setShowForm(true); }}
                className="bg-primary text-primary-foreground hover:bg-foreground"
              >
                <Plus size={18} className="mr-2" />
                New Alteration
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/70 flex items-start justify-center z-50 p-4 overflow-y-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-card border border-white/10 p-6 max-w-4xl w-full my-8"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-serif text-xl">
                {editingOrder ? "Edit Alteration" : "New Alteration Order"}
              </h2>
              <button
                onClick={() => { setShowForm(false); setEditingOrder(null); }}
                className="text-muted-foreground hover:text-foreground"
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Import Options */}
              <div className="flex flex-wrap gap-3 p-4 bg-white/5 border border-white/10">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowMTMPicker(true)}
                  className="border-primary/50 text-primary hover:bg-primary/10"
                >
                  <Scissors size={16} className="mr-2" />
                  Load from MTM Order
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowMeasurementPicker(true)}
                  className="border-white/20"
                >
                  <FileText size={16} className="mr-2" />
                  Import from Submitted Measurements
                </Button>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleImageUpload}
                  accept="image/*"
                  multiple
                  className="hidden"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  className="border-white/20"
                >
                  <Image size={16} className="mr-2" />
                  Add Reference Image{uploadedImages.length > 0 ? 's' : ''}
                </Button>
                {uploadedImages.length > 0 && (
                  <span className="text-sm text-green-400 flex items-center gap-1">
                    <Check size={14} /> {uploadedImages.length} image{uploadedImages.length > 1 ? 's' : ''} added
                  </span>
                )}
              </div>

              {/* Measurement Picker Modal */}
              {showMeasurementPicker && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]">
                  <div className="bg-card border border-white/10 p-6 max-w-lg w-full max-h-[70vh] overflow-y-auto mx-4">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-serif text-lg">Select Measurement Submission</h3>
                      <button type="button" onClick={() => setShowMeasurementPicker(false)}>
                        <X size={20} />
                      </button>
                    </div>
                    {measurements.length === 0 ? (
                      <p className="text-muted-foreground text-center py-8">No measurements submitted yet</p>
                    ) : (
                      <div className="space-y-2">
                        {measurements.map((m, idx) => (
                          <button
                            key={m.id || idx}
                            type="button"
                            onClick={() => importFromMeasurement(m)}
                            className="w-full p-3 text-left bg-white/5 hover:bg-white/10 transition-colors border border-white/10"
                          >
                            <p className="font-medium">{m.full_name || m.name || "Unknown"}</p>
                            <p className="text-sm text-muted-foreground">{m.email}</p>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* MTM Order Picker Modal */}
              {showMTMPicker && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]">
                  <div className="bg-card border border-white/10 p-6 max-w-lg w-full max-h-[70vh] overflow-y-auto mx-4">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-serif text-lg">Load from MTM Order</h3>
                      <button type="button" onClick={() => setShowMTMPicker(false)}>
                        <X size={20} />
                      </button>
                    </div>
                    <p className="text-sm text-muted-foreground mb-4">
                      Select an order to load its measurements into this alteration form.
                    </p>
                    {mtmOrders.length === 0 ? (
                      <p className="text-muted-foreground text-center py-8">No submitted MTM orders found</p>
                    ) : (
                      <div className="space-y-2">
                        {mtmOrders.map((order) => (
                          <button
                            key={order.id}
                            type="button"
                            onClick={() => importFromMTMOrder(order)}
                            className="w-full p-3 text-left bg-white/5 hover:bg-primary/10 transition-colors border border-white/10 hover:border-primary/30"
                          >
                            <div className="flex justify-between items-start">
                              <div>
                                <p className="font-mono text-primary">{order.order_number}</p>
                                <p className="font-medium">{order.client_name}</p>
                              </div>
                              <div className="text-right text-xs">
                                <span className={`px-2 py-0.5 rounded ${
                                  order.status === 'submitted' ? 'bg-blue-500/20 text-blue-400' :
                                  order.status === 'complete' ? 'bg-green-500/20 text-green-400' :
                                  'bg-yellow-500/20 text-yellow-400'
                                }`}>
                                  {order.status}
                                </span>
                              </div>
                            </div>
                            <div className="mt-2 flex gap-2 text-xs text-muted-foreground">
                              {order.jacket_measurements && <span className="px-1.5 py-0.5 bg-blue-500/20 text-blue-400 rounded">Jacket</span>}
                              {order.pant_measurements && <span className="px-1.5 py-0.5 bg-red-500/20 text-red-400 rounded">Trousers</span>}
                              {order.vest_measurements && <span className="px-1.5 py-0.5 bg-purple-500/20 text-purple-400 rounded">Vest</span>}
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Basic Info */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm mb-2">TAG / Order # *</label>
                  <Input
                    value={formData.tag}
                    onChange={(e) => setFormData({ ...formData, tag: e.target.value })}
                    placeholder="MYWCH25-35816-C ALT"
                    className="bg-background border-white/10 font-mono"
                  />
                </div>
                <div>
                  <label className="block text-sm mb-2">Due Date *</label>
                  <Input
                    type="date"
                    value={formData.due_date}
                    onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                    className="bg-background border-white/10"
                  />
                </div>
                <div>
                  <label className="block text-sm mb-2">Client Name</label>
                  <Input
                    value={formData.client_name}
                    onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                    placeholder="Client name"
                    className="bg-background border-white/10"
                  />
                </div>
                <div>
                  <label className="block text-sm mb-2">Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className="w-full bg-background border border-white/10 rounded-md px-3 py-2 text-sm"
                  >
                    <option value="pending">Pending</option>
                    <option value="in_progress">In Progress</option>
                    <option value="completed">Completed</option>
                  </select>
                </div>
                
                {/* Free Alteration Toggle */}
                <div>
                  <label className="block text-sm text-muted-foreground mb-2">Pricing</label>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.is_free}
                      onChange={(e) => setFormData(prev => ({ ...prev, is_free: e.target.checked }))}
                      className="w-5 h-5 rounded border-white/20 bg-background text-primary focus:ring-primary"
                    />
                    <span className="text-sm">Free Alteration (Complimentary)</span>
                  </label>
                </div>
              </div>

              {/* Reference Images Preview (Multiple) */}
              {uploadedImages.length > 0 && (
                <div className="border border-white/10 p-4">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-sm text-muted-foreground">Reference Images ({uploadedImages.length}):</p>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => fileInputRef.current?.click()}
                      className="border-white/20 text-xs"
                    >
                      <Plus size={14} className="mr-1" />
                      Add More
                    </Button>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    {uploadedImages.map((img, idx) => (
                      <div key={idx} className="relative group">
                        <img src={img} alt={`Reference ${idx + 1}`} className="h-32 w-full object-cover rounded border border-white/10" />
                        <button
                          type="button"
                          onClick={() => removeImage(idx)}
                          className="absolute top-1 right-1 p-1 bg-red-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X size={14} className="text-white" />
                        </button>
                        <span className="absolute bottom-1 left-1 text-xs bg-black/60 px-1 rounded">{idx + 1}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* MEASUREMENT REFERENCE SECTIONS */}
              <div className="space-y-4">
                {/* Jacket Measurements */}
                <div className="border border-white/10">
                  <button
                    type="button"
                    onClick={() => toggleSection('jacket_measurements')}
                    className="w-full flex items-center justify-between p-4 bg-primary/10 hover:bg-primary/20 transition-colors"
                  >
                    <span className="font-serif text-lg">JACKET MEASUREMENTS</span>
                    {expandedSections.jacket_measurements ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                  </button>
                  {expandedSections.jacket_measurements && (
                    <div className="p-4 overflow-x-auto">
                      <table className="w-full text-sm min-w-[600px]">
                        <thead>
                          <tr className="border-b border-white/10 text-left text-muted-foreground">
                            <th className="pb-2 w-8"></th>
                            <th className="pb-2">Measure</th>
                            <th className="pb-2 w-24">Prev ALT</th>
                            <th className="pb-2 w-28">Adjust (+/-)</th>
                            <th className="pb-2 w-24">Current ALT</th>
                            <th className="pb-2 w-20 text-right">Fee</th>
                          </tr>
                        </thead>
                        <tbody>
                          {formData.jacket_measurements.map((item, idx) => (
                            <tr key={idx} className="border-b border-white/5">
                              <td className="py-2">
                                <input
                                  type="checkbox"
                                  checked={item.selected}
                                  onChange={(e) => updateAlterationItem('jacket_measurements', idx, 'selected', e.target.checked)}
                                  className="rounded border-white/20"
                                />
                              </td>
                              <td className="py-2 font-medium">{item.name}</td>
                              <td className="py-2">
                                <Input
                                  value={item.prev_value}
                                  onChange={(e) => updateAlterationItem('jacket_measurements', idx, 'prev_value', e.target.value)}
                                  className="bg-background border-white/10 h-8 text-sm"
                                  placeholder="0"
                                />
                              </td>
                              <td className="py-2">
                                <select
                                  value={item.adjustment || "0"}
                                  onChange={(e) => handleAdjustmentChange('jacket_measurements', idx, e.target.value)}
                                  className="w-full bg-background border border-white/10 rounded-md px-2 py-1 text-sm h-8"
                                >
                                  {ADJUSTMENT_OPTIONS.map(opt => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                  ))}
                                </select>
                              </td>
                              <td className="py-2">
                                <Input
                                  value={item.current_value}
                                  onChange={(e) => updateAlterationItem('jacket_measurements', idx, 'current_value', e.target.value)}
                                  className="bg-background border-white/10 h-8 text-sm"
                                  placeholder="0"
                                />
                              </td>
                              <td className="py-2 text-right text-primary">${item.fee.toFixed(2)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                {/* Pants Measurements */}
                <div className="border border-white/10">
                  <button
                    type="button"
                    onClick={() => toggleSection('pants_measurements')}
                    className="w-full flex items-center justify-between p-4 bg-primary/10 hover:bg-primary/20 transition-colors"
                  >
                    <span className="font-serif text-lg">PANTS MEASUREMENTS</span>
                    {expandedSections.pants_measurements ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                  </button>
                  {expandedSections.pants_measurements && (
                    <div className="p-4 overflow-x-auto">
                      <table className="w-full text-sm min-w-[600px]">
                        <thead>
                          <tr className="border-b border-white/10 text-left text-muted-foreground">
                            <th className="pb-2 w-8"></th>
                            <th className="pb-2">Measure</th>
                            <th className="pb-2 w-24">Prev ALT</th>
                            <th className="pb-2 w-28">Adjust (+/-)</th>
                            <th className="pb-2 w-24">Current ALT</th>
                            <th className="pb-2 w-20 text-right">Fee</th>
                          </tr>
                        </thead>
                        <tbody>
                          {formData.pants_measurements.map((item, idx) => (
                            <tr key={idx} className="border-b border-white/5">
                              <td className="py-2">
                                <input
                                  type="checkbox"
                                  checked={item.selected}
                                  onChange={(e) => updateAlterationItem('pants_measurements', idx, 'selected', e.target.checked)}
                                  className="rounded border-white/20"
                                />
                              </td>
                              <td className="py-2 font-medium">{item.name}</td>
                              <td className="py-2">
                                <Input
                                  value={item.prev_value}
                                  onChange={(e) => updateAlterationItem('pants_measurements', idx, 'prev_value', e.target.value)}
                                  className="bg-background border-white/10 h-8 text-sm"
                                  placeholder="0"
                                />
                              </td>
                              <td className="py-2">
                                <select
                                  value={item.adjustment || "0"}
                                  onChange={(e) => handleAdjustmentChange('pants_measurements', idx, e.target.value)}
                                  className="w-full bg-background border border-white/10 rounded-md px-2 py-1 text-sm h-8"
                                >
                                  {ADJUSTMENT_OPTIONS.map(opt => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                  ))}
                                </select>
                              </td>
                              <td className="py-2">
                                <Input
                                  value={item.current_value}
                                  onChange={(e) => updateAlterationItem('pants_measurements', idx, 'current_value', e.target.value)}
                                  className="bg-background border-white/10 h-8 text-sm"
                                  placeholder="0"
                                />
                              </td>
                              <td className="py-2 text-right text-primary">${item.fee.toFixed(2)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                {/* Vest Measurements */}
                <div className="border border-white/10">
                  <button
                    type="button"
                    onClick={() => toggleSection('vest_measurements')}
                    className="w-full flex items-center justify-between p-4 bg-primary/10 hover:bg-primary/20 transition-colors"
                  >
                    <span className="font-serif text-lg">VEST MEASUREMENTS</span>
                    {expandedSections.vest_measurements ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                  </button>
                  {expandedSections.vest_measurements && (
                    <div className="p-4 overflow-x-auto">
                      <table className="w-full text-sm min-w-[600px]">
                        <thead>
                          <tr className="border-b border-white/10 text-left text-muted-foreground">
                            <th className="pb-2 w-8"></th>
                            <th className="pb-2">Measure</th>
                            <th className="pb-2 w-24">Prev ALT</th>
                            <th className="pb-2 w-28">Adjust (+/-)</th>
                            <th className="pb-2 w-24">Current ALT</th>
                            <th className="pb-2 w-20 text-right">Fee</th>
                          </tr>
                        </thead>
                        <tbody>
                          {formData.vest_measurements.map((item, idx) => (
                            <tr key={idx} className="border-b border-white/5">
                              <td className="py-2">
                                <input
                                  type="checkbox"
                                  checked={item.selected}
                                  onChange={(e) => updateAlterationItem('vest_measurements', idx, 'selected', e.target.checked)}
                                  className="rounded border-white/20"
                                />
                              </td>
                              <td className="py-2 font-medium">{item.name}</td>
                              <td className="py-2">
                                <Input
                                  value={item.prev_value}
                                  onChange={(e) => updateAlterationItem('vest_measurements', idx, 'prev_value', e.target.value)}
                                  className="bg-background border-white/10 h-8 text-sm"
                                  placeholder="0"
                                />
                              </td>
                              <td className="py-2">
                                <select
                                  value={item.adjustment || "0"}
                                  onChange={(e) => handleAdjustmentChange('vest_measurements', idx, e.target.value)}
                                  className="w-full bg-background border border-white/10 rounded-md px-2 py-1 text-sm h-8"
                                >
                                  {ADJUSTMENT_OPTIONS.map(opt => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                  ))}
                                </select>
                              </td>
                              <td className="py-2">
                                <Input
                                  value={item.current_value}
                                  onChange={(e) => updateAlterationItem('vest_measurements', idx, 'current_value', e.target.value)}
                                  className="bg-background border-white/10 h-8 text-sm"
                                  placeholder="0"
                                />
                              </td>
                              <td className="py-2 text-right text-primary">${item.fee.toFixed(2)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>

              {/* Total & Actions */}
              <div className="flex items-center justify-between pt-4 border-t border-white/10">
                <div className="text-xl">
                  <span className="text-muted-foreground">Total:</span>
                  <span className="font-serif text-primary ml-2">${calculateTotal().toFixed(2)}</span>
                </div>
                <div className="flex gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => { setShowForm(false); setEditingOrder(null); }}
                    className="border-white/20"
                  >
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-primary text-primary-foreground hover:bg-foreground">
                    {editingOrder ? "Update" : "Create"} Alteration
                  </Button>
                </div>
              </div>
            </form>
          </motion.div>
        </div>
      )}

      {/* Orders List */}
      <section className="py-6">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent mx-auto mb-3" />
              <p className="text-muted-foreground">Loading alterations...</p>
            </div>
          ) : orders.length === 0 ? (
            <div className="text-center py-12 bg-card border border-white/10">
              <Scissors size={48} className="mx-auto mb-4 text-muted-foreground opacity-50" />
              <p className="text-muted-foreground mb-4">No alteration orders yet</p>
              <Button onClick={() => setShowForm(true)} variant="outline" className="border-white/20">
                <Plus size={16} className="mr-2" />
                Create First Alteration
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {orders.map((order, index) => {
                const daysUntil = getDaysUntilDue(order.due_date);
                const selectedCount = [
                  ...(order.jacket_basic || []),
                  ...(order.jacket_additional || []),
                  ...(order.pants || [])
                ].filter(i => i.selected).length;

                return (
                  <motion.div
                    key={order.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className={`bg-card border p-6 ${
                      order.status === "completed" ? "border-green-500/30" :
                      daysUntil <= 3 ? "border-red-500/30" : "border-white/10"
                    }`}
                  >
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex items-start gap-4">
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                          order.status === "completed" ? "bg-green-500/10" : "bg-primary/10"
                        }`}>
                          <Scissors size={20} className={order.status === "completed" ? "text-green-400" : "text-primary"} />
                        </div>
                        <div>
                          <div className="flex items-center gap-3">
                            <h3 className="font-mono font-medium text-lg">{order.tag}</h3>
                            <span className={`text-xs px-2 py-0.5 rounded ${STATUS_COLORS[order.status]}`}>
                              {order.status.replace("_", " ").toUpperCase()}
                            </span>
                          </div>
                          {order.client_name && (
                            <p className="text-sm text-muted-foreground">{order.client_name}</p>
                          )}
                          <p className="text-sm text-muted-foreground mt-1">
                            {selectedCount} alterations selected
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-6">
                        <div className="text-center">
                          <p className="text-xs text-muted-foreground mb-1">Due Date</p>
                          <p className="font-medium">{formatDate(order.due_date)}</p>
                          {order.status !== "completed" && (
                            <p className={`text-xs mt-1 ${
                              daysUntil < 0 ? "text-red-400" :
                              daysUntil <= 3 ? "text-yellow-400" : "text-muted-foreground"
                            }`}>
                              {daysUntil < 0 ? `${Math.abs(daysUntil)} days overdue` :
                               daysUntil === 0 ? "Due today" :
                               `${daysUntil} days`}
                            </p>
                          )}
                        </div>
                        <div className="text-center">
                          <p className="text-xs text-muted-foreground mb-1">Total Fee</p>
                          <p className="font-serif text-xl text-primary">${(order.total_fee || 0).toFixed(2)}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleEdit(order)}
                            className="p-2 text-muted-foreground hover:text-foreground transition-colors"
                            title="Edit"
                          >
                            <Pencil size={18} />
                          </button>
                          <button
                            onClick={() => handleDelete(order.id)}
                            className="p-2 text-muted-foreground hover:text-red-400 transition-colors"
                            title="Delete"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default AdminAlterationsPage;
